// material-ui
import FormControl from '@mui/material/FormControl';
import Box from '@mui/material/Box';
import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import dashboard from '../../../../menu-items/dashboard';
import { auth, toastifyError } from '../../../../../helper/Utility';
import debounce from 'lodash/debounce';

export default function Search() {
  const role = auth("admin")?.user_role;
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [arrDataToFilter, setArrDataToFilter] = useState([]);
  const [filteredSuggestions, setFilteredSuggestions] = useState([]);
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(0);
  const suggestionsRef = useRef(null);
  const flattenMenu = (menuItems, parentTitle = '') => {
    const result = [];
    menuItems?.forEach((item) => {
      const breadcrumb = parentTitle ? `${parentTitle} / ${item.title}` : item.title;
      result.push({ ...item, breadcrumb });
      if (Array.isArray(item.children)) {
        item.children.forEach((child) => {
          result.push({
            ...child,
            breadcrumb: `${breadcrumb} / ${child.title}`,
          });
        });
      }
    });
    setArrDataToFilter(result);
  };

  useEffect(() => {
    if (role === "admin") {
      flattenMenu(dashboard?.children);
    }
  }, []);

  const arrDataToFilterRef = useRef(arrDataToFilter);
  useEffect(() => {
    arrDataToFilterRef.current = arrDataToFilter;
  }, [arrDataToFilter]);

  const debouncedSearch = useRef(
    debounce((query) => {
      const matches = arrDataToFilterRef.current.filter((item) =>
        item?.title?.toLowerCase().includes(query.toLowerCase())
      );
      setFilteredSuggestions(matches);
    }, 300)
  ).current;

  useEffect(() => {
    debouncedSearch(searchTerm);
    return () => {
      debouncedSearch.cancel();
    };
  }, [searchTerm]);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setActiveSuggestionIndex(0);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    const matchedItem = arrDataToFilter.find(item =>
      item.title.toLowerCase().includes(searchTerm.toLowerCase())
    );
    if (matchedItem) {
      navigate(matchedItem.url);
      setSearchTerm('');
      setFilteredSuggestions([]);
    } else {
      toastifyError("Oops! No Matching Result");
    }
  };

  const handleSuggestionClick = (item) => {
    navigate(item.url);
    setSearchTerm('');
    setFilteredSuggestions([]);
  };

  const handleKeyDown = (e) => {
    if (filteredSuggestions.length === 0) return;

    if (e.key === 'ArrowDown') {
      setActiveSuggestionIndex((prev) =>
        prev === filteredSuggestions.length - 1 ? 0 : prev + 1
      );
    } else if (e.key === 'ArrowUp') {
      setActiveSuggestionIndex((prev) =>
        prev === 0 ? filteredSuggestions.length - 1 : prev - 1
      );
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const selectedItem = filteredSuggestions[activeSuggestionIndex];
      if (selectedItem) {
        handleSuggestionClick(selectedItem);
      }
    }
  };

  return (
    <Box sx={{ width: '100%', ml: { xs: 0, md: 1 }, position: 'relative' }} className="searchbr-custom">
      <FormControl sx={{ width: { xs: '100%', md: 300 } }}>
        <form onSubmit={handleSearchSubmit} autoComplete="off">
          <div className="twitter-typeahead">
            <input
              type="text"
              className="form-control search-input"
              placeholder="Search..."
              aria-label="Search..."
              value={searchTerm}
              onChange={handleSearchChange}
              onKeyDown={handleKeyDown}
            />
            {searchTerm && (
              <i
                className="ti ti-x ti-sm search-toggler cursor-pointer"
                onClick={() => {
                  setSearchTerm('');
                  setFilteredSuggestions([]);
                }}
              />
            )}
          </div>

          {searchTerm && filteredSuggestions.length > 0 && (
            <ul className="search-suggestion-list" ref={suggestionsRef}>
              {filteredSuggestions.map((item, index) => (
                <li
                  key={index}
                  className={index === activeSuggestionIndex ? 'active' : ''}
                  onClick={() => handleSuggestionClick(item)}
                >
                 {item.breadcrumb}
                </li>
              ))}
            </ul>
          )}
        </form>
      </FormControl>
      <style>{`
        .search-suggestion-list {
          list-style: none;
          margin: 0;
          padding: 0;
          position: absolute;
          top: 100%;
          left: 0;
          right: 0;
          background: #fff;
          border: 1px solid #ccc;
          z-index: 10;
          max-height: 200px;
          overflow-y: auto;
          border-radius: 4px;
        }

        .search-suggestion-list li {
          padding: 7px 10px;
          cursor: pointer;
          font-weight : bold
        }

        .search-suggestion-list li.active,
        .search-suggestion-list li:hover {
          background-color: #f0f0f0;
        }
      `}</style>
    </Box>
  );
}
